/**
 * BillingPage.tsx — /app/billing — current tier, usage, and upgrade
 *
 * Purpose:    Show current tier + usage meters and a tier comparison table.
 *             Prices always come from ./pricing.ts (SPORT-driven) — never
 *             hardcoded here.
 * Inputs:     billingApi from @/lib/saas-client; TIERS from @/lib/tiers;
 *             PRICING_TIERS from @/lib/pricing
 * Outputs:    Billing/usage UI + checkout CTA (real or "coming soon")
 * Constraints: Dark-first styling; WCAG AA
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { billingApi, type BillingInfo } from '@/lib/saas-client'
import { TIERS, TIER_IDS, usagePct, type TierId } from '@/lib/tiers'
import { PRICING_TIERS, formatPrice } from '@/lib/pricing'
import type { SentryError } from '@/lib/sentry-error'

// Live Stripe billing shipped (W4) — checkout defaults ON; set
// VITE_STRIPE_ENABLED=false to fall back to the self-host-only CTA.
const STRIPE_ENABLED = import.meta.env.VITE_STRIPE_ENABLED !== 'false'

function priceLabel(pricingId: string): string {
  const tier = PRICING_TIERS.find((t) => t.id === pricingId)
  if (!tier || !tier.price) return 'Free'
  return `${formatPrice(tier.price.monthly)}/mo`
}

/**
 * Call a same-origin /api/billing function and return the redirect URL.
 * These Vercel functions verify the session cookie and proxy ping_api
 * (the Stripe authority) — the SPA never talks to Stripe directly.
 */
async function billingRedirect(path: string, body?: unknown): Promise<{ url?: string; error?: string }> {
  try {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      credentials: 'include',
    })
    const json = (await res.json().catch(() => ({}))) as { url?: string; error?: string }
    if (!res.ok || !json.url) return { error: json.error || 'Billing is temporarily unavailable. Try again shortly.' }
    return { url: json.url }
  } catch {
    return { error: 'Billing is temporarily unavailable. Try again shortly.' }
  }
}

export function BillingPage() {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<AsyncState>('loading')
  const [billing, setBilling] = useState<BillingInfo | null>(null)
  const [error, setError] = useState<SentryError | undefined>()
  const [checkingOut, setCheckingOut] = useState<TierId | null>(null)
  const [openingPortal, setOpeningPortal] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const checkoutStatus = searchParams.get('status')

  async function load() {
    setState('loading')
    // GET /v1/usage is the dedicated usage endpoint; when it answers, its
    // numbers win over the billing envelope's snapshot. While it is still
    // rolling out (coming-online) the billing envelope's usage stands.
    const [result, usageRes] = await Promise.all([billingApi.get(), billingApi.usage()])
    if (!result.ok) {
      setError(result.error)
      setState('error')
      return
    }
    setBilling(usageRes.ok ? { ...result.value, usage: usageRes.value } : result.value)
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [])

  async function handleUpgrade(tier: TierId) {
    if (tier === 'free' || !STRIPE_ENABLED) return
    setBillingError(null)
    setCheckingOut(tier)
    const result = await billingRedirect('/api/billing/checkout', { tier, cadence: 'monthly' })
    setCheckingOut(null)
    if (result.url) {
      window.location.href = result.url
    } else {
      setBillingError(result.error ?? null)
    }
  }

  async function handleManageBilling() {
    setBillingError(null)
    setOpeningPortal(true)
    const result = await billingRedirect('/api/billing/portal')
    setOpeningPortal(false)
    if (result.url) {
      window.location.href = result.url
    } else {
      setBillingError(result.error ?? null)
    }
  }

  return (
    <div className="p-6 lg:p-8">
      <h1 className="mb-6 text-xl font-semibold text-brand-ink">Billing</h1>

      {checkoutStatus === 'success' && (
        <p className="mb-4 rounded-lg border border-green-200 dark:border-green-800/60 bg-green-50 dark:bg-green-950/40 px-4 py-3 text-sm text-green-700 dark:text-green-300" role="status">
          Payment received — your plan activates within a minute. Your self-host license key is on its way by email too.
        </p>
      )}
      {checkoutStatus === 'cancelled' && (
        <p className="mb-4 rounded-lg border border-brand-card bg-brand-surface px-4 py-3 text-sm text-brand-secondary" role="status">
          Checkout cancelled — no charge was made.
        </p>
      )}
      {billingError && (
        <p className="mb-4 rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300" role="alert">
          {billingError}
        </p>
      )}

      <AsyncScreen state={state} error={error} onRetry={load}>
        {billing && (
          <>
            <section className="mb-8 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Current usage">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-muted">
                  Current plan — {TIERS[billing.tier].label}
                </h2>
                {billing.tier !== 'free' && (
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={openingPortal}
                    className="rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50"
                  >
                    {openingPortal ? 'Opening…' : 'Manage billing'}
                  </button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <UsageMeter label="Monitors" used={billing.usage.monitors} max={TIERS[billing.tier].monitors.max} />
                <UsageMeter label="Status pages" used={billing.usage.statusPages} max={TIERS[billing.tier].statusPages} />
                <UsageMeter label="Error events" used={billing.usage.errorEventsThisMonth} max={TIERS[billing.tier].errorEventsPerMonth} />
                <UsageMeter label="RUM pageviews" used={billing.usage.rumPageviewsThisMonth} max={TIERS[billing.tier].rumPageviewsPerMonth} />
              </div>
            </section>

            <section aria-label="Plan comparison">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Plans</h2>
              <div className="grid gap-4 lg:grid-cols-3">
                {TIER_IDS.map((tierId) => {
                  const quota = TIERS[tierId]
                  const isCurrent = billing.tier === tierId
                  return (
                    <div key={tierId} className={`rounded-xl border p-6 ${isCurrent ? 'border-sky-600 bg-sky-50 dark:bg-sky-950/20' : 'border-brand-subtle bg-brand-surface'}`}>
                      <h3 className="text-base font-semibold text-brand-ink">{quota.label}</h3>
                      <p className="mt-1 text-2xl font-bold text-brand-ink">{priceLabel(quota.pricingId)}</p>
                      <ul className="mt-4 space-y-1.5 text-sm text-brand-muted">
                        <li>{quota.monitors.max} monitors @ {quota.monitors.minIntervalSeconds / 60}min</li>
                        <li>{quota.statusPages} status page{quota.statusPages === 1 ? '' : 's'}</li>
                        <li>{quota.retentionDays}-day retention</li>
                        <li>{quota.errorEventsPerMonth.toLocaleString()} error events/mo</li>
                        <li>{quota.rumPageviewsPerMonth.toLocaleString()} RUM pageviews/mo</li>
                        <li>{quota.apiAccess === 'full' ? 'Full' : 'Read-only'} API access</li>
                        <li>{quota.seats} seat{quota.seats === 1 ? '' : 's'}</li>
                      </ul>
                      {isCurrent ? (
                        <p className="mt-5 text-center text-xs font-medium text-sky-700 dark:text-sky-400">Current plan</p>
                      ) : tierId === 'free' ? null : STRIPE_ENABLED ? (
                        <button
                          type="button"
                          onClick={() => handleUpgrade(tierId)}
                          disabled={checkingOut === tierId}
                          className="mt-5 w-full rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50"
                        >
                          {checkingOut === tierId ? 'Redirecting…' : `Upgrade to ${quota.label}`}
                        </button>
                      ) : (
                        <div className="mt-5">
                          <button
                            type="button"
                            disabled
                            className="w-full cursor-not-allowed rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-muted"
                          >
                            Checkout opens soon
                          </button>
                          <p className="mt-2 text-center text-xs text-brand-muted">
                            Self-host today with <code className="text-brand-muted">nself bundle install sentry</code>.{' '}
                            <a href="https://nself.org/sentry" className="text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">Learn more</a>
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          </>
        )}
      </AsyncScreen>
    </div>
  )
}

function UsageMeter({ label, used, max }: { label: string; used: number; max: number }) {
  const pct = usagePct(used, max)
  return (
    <div>
      <p className="mb-1 flex items-baseline justify-between text-xs text-brand-muted">
        <span>{label}</span>
        <span>{used.toLocaleString()} / {max.toLocaleString()}</span>
      </p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand-surface-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-sky-500'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
